import type { FC, ReactNode } from "react";
import { useCallback, useState } from "react";

import type { Dayjs } from "dayjs";
import isFunction from "lodash/isFunction";

import { YearCalendarWithSelection, IconButton, Button } from "@similarweb/ui-components";

import { withAutoReposition } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/steps/withAutoReposition";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { formatDuration } from "app/components/duration-selector/DurationSelectorSimple";
import { getCompareDurationSelected } from "app/components/duration-selector/DurationSelectorUtils";

import { GranularitySwitch } from "../GranularitySwitch";
import { MoreDataBanner } from "../MoreDataBanner";

import * as SC from "./StyledComponents";

interface ICalendarStep {
    closePopup?: VoidFunction;
    onBack: VoidFunction;
    minDate: Dayjs;
    maxDate: Dayjs;
    onSubmit: (duration: any) => void;
    initialDuration: { from: Dayjs; to: Dayjs };
    showWeeklyToggle?: boolean;
    isWeeklyDataAvailable?: boolean;
    weeklyToggleProps?: {
        weekly?: ReactNode;
        monthly?: ReactNode;
    };
    onGranularityToggleClick?: (overrideTrackingKey: string) => void;
}

const CalendarStep: FC<ICalendarStep> = ({
    closePopup,
    onSubmit,
    onBack,
    minDate,
    maxDate,
    initialDuration,
    showWeeklyToggle,
    isWeeklyDataAvailable,
    weeklyToggleProps,
    onGranularityToggleClick,
}) => {
    const translate = useTranslation();
    const durationToCompareDuration = (duration: FixmeType) => getCompareDurationSelected(duration, false, null);

    const [selectedCompareDuration, setSelectedCompareDuration] = useState(() =>
        durationToCompareDuration(initialDuration),
    );

    const onSelectionChange = (duration: FixmeType) => setSelectedCompareDuration(durationToCompareDuration(duration));

    const handlePopupClose = useCallback(() => {
        if (isFunction(closePopup)) {
            closePopup();
        }
    }, [closePopup]);

    const submitAndClose = useCallback(() => {
        onSubmit(formatDuration(selectedCompareDuration.primary));
        handlePopupClose();
    }, [onSubmit, handlePopupClose, selectedCompareDuration]);

    return (
        <>
            {showWeeklyToggle && !isWeeklyDataAvailable ? (
                <SC.GranularitySwitchContainer>
                    <SC.BackSectionGranularitySwitch>
                        <IconButton
                            type="flat"
                            iconSize="xs"
                            iconName="arrow-left"
                            onClick={onBack}
                        />
                        <SC.BackText>{translate("duration_selector.custom.title")}</SC.BackText>
                    </SC.BackSectionGranularitySwitch>
                    <GranularitySwitch
                        onGranularityToggleClick={() => onGranularityToggleClick?.("WeeklyKeywords/datepickercustom")}
                        weeklyToggleProps={weeklyToggleProps}
                    />
                </SC.GranularitySwitchContainer>
            ) : (
                <SC.BackSection>
                    <IconButton
                        type="flat"
                        iconSize="xs"
                        iconName="arrow-left"
                        onClick={onBack}
                    />
                    <SC.BackText>{translate("duration_selector.custom.title")}</SC.BackText>
                </SC.BackSection>
            )}
            <YearCalendarWithSelection
                minDate={minDate}
                maxDate={maxDate}
                onSelectionChange={onSelectionChange}
                // @ts-ignore FIXME: strict migration - Type '{ primary: any; secondary: { from: any; to: any; } | null; }' is not assignable to type 'IComparedDuration' — value comes from getCompareDurationSelected (untyped util), IComparedDuration is external @similarweb/ui-components
                selection={selectedCompareDuration}
                hasPermissionsLock={false}
                renderContactUs={() => null}
                hasVerticalLineSeperator={true}
            />
            <SC.BottomSection>
                <Button
                    type="primary"
                    onClick={submitAndClose}
                >
                    {translate("duration_selector.custom.apply")}
                </Button>
            </SC.BottomSection>
            <div>
                <MoreDataBanner
                    large={true}
                    onClick={handlePopupClose}
                />
            </div>
        </>
    );
};

export default withAutoReposition(CalendarStep);
